'use client';

import React, { ReactNode } from 'react';
import { StyledMcpUseCases } from './mcpUseCases.styles';

interface IUseCase {
	icon: ReactNode;
	title: string;
	outcome: string;
	widgets: string[];
	prompt: string;
}

const S = 1.7; // shared stroke width

const useCases: IUseCase[] = [
	{
		icon: (
			<svg viewBox="0 0 24 24" strokeWidth={S} strokeLinecap="round" strokeLinejoin="round">
				<circle cx="9" cy="8" r="3.2" />
				<path d="M2.5 19c0-3.3 2.9-5.5 6.5-5.5s6.5 2.2 6.5 5.5" />
				<path d="M16 6.2a3 3 0 0 1 0 5.6M18.5 19c0-2.4-1.3-4.3-3.3-5.2" />
			</svg>
		),
		title: 'Grow your audience',
		outcome: 'Pull your social content onto your site automatically.',
		widgets: ['Instagram Feed', 'TikTok Feed', 'YouTube Feed', 'Social Links'],
		prompt: 'Add our Instagram feed to the homepage.',
	},
	{
		icon: (
			<svg viewBox="0 0 24 24" strokeWidth={S} strokeLinecap="round" strokeLinejoin="round">
				<path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6z" />
				<path d="M9 12l2 2 4-4" />
			</svg>
		),
		title: 'Win trust',
		outcome: 'Show off your reputation with real social proof.',
		widgets: ['Google Reviews', 'Testimonials', 'Trust Badges', 'Social Proof'],
		prompt: 'Embed our Google reviews above the footer.',
	},
	{
		icon: (
			<svg viewBox="0 0 24 24" strokeWidth={S} strokeLinecap="round" strokeLinejoin="round">
				<rect x="3" y="5" width="18" height="14" rx="2.5" />
				<path d="M3.5 7l8.5 6 8.5-6" />
			</svg>
		),
		title: 'Capture leads',
		outcome: 'Turn visitors into contacts you can reach again.',
		widgets: ['Contact Form', 'Email Signup', 'Popup Builder', 'Spinning Wheel'],
		prompt: 'Create a newsletter signup popup.',
	},
	{
		icon: (
			<svg viewBox="0 0 24 24" strokeWidth={S} strokeLinecap="round" strokeLinejoin="round">
				<path d="M3.5 12.5l8-8.5h6a3 3 0 0 1 3 3v6l-8.5 8a2 2 0 0 1-2.8 0l-5.7-5.7a2 2 0 0 1 0-2.8z" />
				<circle cx="15.5" cy="8.5" r="1.4" />
			</svg>
		),
		title: 'Drive sales',
		outcome: 'Create urgency and move shoppers to checkout.',
		widgets: ['Countdown', 'Coupon Popup', 'Pricing Tables', 'Payment Button'],
		prompt: 'Add a Black Friday countdown bar.',
	},
	{
		icon: (
			<svg viewBox="0 0 24 24" strokeWidth={S} strokeLinecap="round" strokeLinejoin="round">
				<path d="M4 5h16v11H8l-4 4z" />
				<path d="M8.5 10.5h0M12 10.5h0M15.5 10.5h0" />
			</svg>
		),
		title: 'Engage visitors',
		outcome: 'Make your page interactive and keep people on it.',
		widgets: ['Quiz Maker', 'Poll', 'FAQ', 'Before & After'],
		prompt: 'Build a 5-question product quiz.',
	},
	{
		icon: (
			<svg viewBox="0 0 24 24" strokeWidth={S} strokeLinecap="round" strokeLinejoin="round">
				<circle cx="12" cy="12" r="8.5" />
				<circle cx="12" cy="12" r="3.2" />
				<path d="M5.9 5.9l3.6 3.6M14.5 14.5l3.6 3.6M18.1 5.9l-3.6 3.6M9.5 14.5l-3.6 3.6" />
			</svg>
		),
		title: 'Inform & support',
		outcome: 'Answer questions and guide visitors instantly.',
		widgets: ['AI Chatbot', 'WhatsApp Chat', 'Google Maps', 'Opening Hours'],
		prompt: 'Add a WhatsApp chat button.',
	},
];

export const McpUseCases = () => {
	return (
		<StyledMcpUseCases>
			<div className="head">
				<span className="eyebrow">Use cases</span>
				<h3>
					Agentic widgets for <span className="highlight">any goal</span>
				</h3>
				<p>
					Whatever your site needs, your AI agent can build it — drawing from
					200+ live widgets across every category. Just describe the outcome.
				</p>
			</div>

			<div className="grid">
				{useCases.map((uc) => (
					<div className="card" key={uc.title}>
						<span className="icon">{uc.icon}</span>
						<h4>{uc.title}</h4>
						<p className="outcome">{uc.outcome}</p>
						<div className="tags">
							{uc.widgets.map((w) => (
								<span key={w}>{w}</span>
							))}
						</div>
						<p className="prompt">{uc.prompt}</p>
					</div>
				))}
			</div>
		</StyledMcpUseCases>
	);
};
